import EndsBadge from "@/app/(custom-header)/prizes/house-v2/components/EndsBadge";
import { useContext } from "react";
import { HouseCompetitionData } from "@/app/(custom-header)/prizes/house-v2/contextProvider";
import { useAppDispatch } from "@/app/redux/hooks";
import { publishModalClasses, publishModalData, publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import HouseModalContent from "@/app/(custom-header)/prizes/house-v2/houseModalContent";
import EnterNowButton from "../EnterNowButton";
import dayjs from "dayjs";
import { useCompetition } from "../../../../redux/features/categories/selectors";
import { useIsMobile } from "@/app/redux/features/device/selectors";
import PrestigeVillageCredit from "@/app/shared-components/PrestigeVillageCredit";

const HomeSpecs = () => {
  const {
    amentiesImages,
    detailsText,
    houseValue,
    monthlyRental,
    competitionState,
    homeSpecsImage,
    overridePrizeTitleSuffix,
  } = useContext(HouseCompetitionData);

  const houseCompetition = useCompetition({
    CompetitionTypePrefix: "HC",
  });
  const dispatch = useAppDispatch();
  const numberFormatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    notation: "compact",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
  const onShowAllAmeneties = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    dispatch(publishModalData({ modalData: <HouseModalContent activeTab={0} /> }));
    dispatch(
      publishModalClasses({
        classNames:
          "rounded-2xl lg:w-[450px] max-lg:w-[calc(100%-theme(spacing.4))] max-lg:top-1/2 max-lg:-translate-y-1/2 max-lg:absolute",
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  let description = "";
  if (detailsText) {
    const regex = new RegExp(/<strong>(.*)<\/strong>/);
    const matches = regex.exec(detailsText);
    description = matches?.[1] ?? "";
  }
  const houseValueAsNumber = Number(houseValue.replace("£", "").replaceAll(",", ""));
  const titleSuffix = overridePrizeTitleSuffix?.trim() || "DREAM HOME DRAW";
  let title = "THE " + numberFormatter.format(houseValueAsNumber) + " " + titleSuffix;
  switch (competitionState) {
    case "over14Weeks": {
      const days = dayjs(houseCompetition.EndDate).diff(dayjs(), "days");
      title += ` CLOSES IN ${days} ${days === 1 ? "day" : "days"}`;
      break;
    }
    case "nextWeek": {
      title += ` CLOSES NEXT WEEK`;
      break;
    }
    case "thisWeek": {
      title += ` CLOSES THIS WEEK`;
      break;
    }
    case "last3Days": {
      title += ` CLOSES IN 3 DAYS`;
      break;
    }
    case "last2Days": {
      title += ` CLOSES TOMORROW`;
      break;
    }
    case "today": {
      title += ` CLOSES THIS TONIGHT`;
      break;
    }
  }

  const isMobile = useIsMobile();
  return (
    <div className={"flex flex-col items-center gap-6 self-stretch font-jost"}>
      {/*wrapper*/}
      <div className={"flex max-lg:flex-col items-center gap-6 lg:gap-10 self-stretch"}>
        {isMobile && (
          <>
            {/*red badge mobile*/}
            {competitionState === "today" || competitionState === "last2Days" ? (
              <div className={"bg-red-1 flex-center py-2 px-4 -skew-x-6"}>
                <span className={"uppercase text-white text-center font-black text-2xl leading-6 skew-x-6"}>
                  FINAL HOURS
                </span>
              </div>
            ) : null}

            {/*title mobile*/}
            <div className={"w-full px-4 text-[#28282B] text-center text-2xl font-black leading-6 uppercase"}>
              {title}
            </div>
          </>
        )}

        {/*banner*/}
        <div className={"w-full aspect-[1/1] relative lg:size-[620px]"}>
          {/*top left badge*/}
          <EndsBadge className={"top-6 right-6"} />

          {/*gradient effect*/}
          <div className={"absolute size-full inset-0 bg-gradient-to-t from-black to-transparent lg:rounded-b-2xl"} />

          {/*bottom info*/}
          <div
            className={
              "flex max-lg:justify-center lg:justify-between items-center gap-6 absolute bottom-0 inset-x-0 pb-6 lg:pb-10 px-4 lg:px-11"
            }>
            <div className={"max-lg:flex-1 flex flex-col items-center gap-2"}>
              <span
                className={
                  "self-stretch text-white text-center text-sm lg:text-2xl font-medium leading-4 lg:leading-7.5"
                }>
                House Value
              </span>
              <span
                className={
                  "self-stretch text-white text-center text-xl lg:text-[28px] font-medium leading-5.5 lg:leading-8"
                }>
                {houseValue}
              </span>
            </div>
            <div className={"max-lg:flex-1 flex flex-col items-center gap-2"}>
              <span
                className={
                  "self-stretch text-white text-center text-sm lg:text-2xl font-medium leading-4 lg:leading-7.5"
                }>
                Monthly Rental
              </span>
              <span
                className={
                  "self-stretch text-white text-center text-xl lg:text-[28px] font-medium leading-5.5 lg:leading-8"
                }>
                {monthlyRental}
              </span>
            </div>
          </div>

          {/*banner image*/}
          <img
            src={homeSpecsImage}
            className={"size-full object-cover aspect-[1/1] lg:rounded-2xl"}
            alt="img"
            width={360}
            height={360}
          />
        </div>

        <div className={"flex w-full flex-col items-start gap-8 max-lg:px-4"}>
          {!isMobile && (
            <>
              {/*red badge desktop*/}
              {competitionState === "today" || competitionState === "last2Days" ? (
                <div className={"bg-red-1 flex-center py-2 px-6 -skew-x-6"}>
                  <span className={"uppercase text-white text-center font-black text-3.5xl leading-6 skew-x-6"}>
                    FINAL HOURS
                  </span>
                </div>
              ) : null}
              {/*title desktop*/}
              <div
                className={
                  "w-full max-lg:px-4 text-[#28282B] max-lg:text-center text-2xl lg:text-3.5xl font-black leading-6 lg:leading-[44px] lg:max-w-[574px] uppercase"
                }>
                {title}
              </div>
            </>
          )}
          {/*inner wrapper for gap purposes*/}
          <div>
            {/*boxes*/}
            <div className={"w-full grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4"}>
              {amentiesImages.map(amenity => (
                <div
                  key={amenity.Text}
                  className={
                    "flex min-h-20 flex-col items-center gap-1 bg-white shadow-[0_1.5px_8px_0_rgba(0,0,0,0.20)] px-2 py-4 rounded-lg"
                  }>
                  <img className={"size-6 lg:size-8 shrink-0"} src={amenity.Icon} />
                  <span className={"text-[#28282B] text-base font-normal leading-5.5 text-center"}>{amenity.Text}</span>
                </div>
              ))}
            </div>

            {/*amenities modal button like link*/}
            <div className={""}>
              <button
                onClick={onShowAllAmeneties}
                className={
                  "m-0 p-0 border-0 text-[#6679B3] text-center text-sm lg:text-base font-medium leading-5 lg:leading-5.5"
                }>
                SHOW ALL AMENITIES »
              </button>
            </div>
          </div>
          {/*partner credit — Prestige & Village*/}
          <PrestigeVillageCredit />

          {/*text*/}
          <div
            className={
              "text-left font-jost text-[#28282B] text-base lg:text-lg leading-5.5 lg:leading-6 font-normal normal-case"
            }>
            {description}
          </div>
          {/*desktop enter now button*/}
          <div className={"max-lg:hidden"}>
            <EnterNowButton text={"ENTER NOW »"} place={"house_new_second_cta"} />
          </div>
        </div>
      </div>

      {/*ADD LOGOS HERE*/}
    </div>
  );
};

export default HomeSpecs;
